import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/AuthContext";
import { useAuth } from "@/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, FileText, Clock, Bell, DownloadSimple, Factory, Scales, User as UserIcon, CheckCircle, XCircle, ShieldCheck, Eye, UploadSimple, ArrowsClockwise, PencilSimple } from "@phosphor-icons/react";
import { DocumentViewerInline } from "../components/DocumentViewer";
import DocumentEditModal from "../components/DocumentEditModal";

const STATUS_STYLES = {
  vigente: "bg-emerald-50 text-emerald-700 border-emerald-200",
  proximo: "bg-amber-50 text-amber-700 border-amber-200",
  vencido: "bg-rose-50 text-rose-700 border-rose-200",
  pendiente: "bg-neutral-100 text-neutral-600 border-neutral-200",
};
const APPROVAL_STYLES = {
  aprobado: { bg:"bg-emerald-50", text:"text-emerald-700", border:"border-emerald-200", label:"Aprobado" },
  rechazado: { bg:"bg-rose-50", text:"text-rose-700", border:"border-rose-200", label:"Rechazado" },
  pendiente: { bg:"bg-amber-50", text:"text-amber-700", border:"border-amber-200", label:"Pendiente de aprobación" },
};
const ALARM_COLORS = { aviso_inicial: "bg-blue-500", preventiva: "bg-amber-500", vencimiento: "bg-rose-500", critica: "bg-rose-700" };
const ACTION_LABELS = {
  submitted: { label:"Carga inicial", icon: UploadSimple, color:"text-blue-600 bg-blue-50 border-blue-200" },
  resubmitted: { label:"Reenviado", icon: ArrowsClockwise, color:"text-indigo-600 bg-indigo-50 border-indigo-200" },
  aprobado: { label:"Aprobado", icon: CheckCircle, color:"text-emerald-600 bg-emerald-50 border-emerald-200" },
  rechazado: { label:"Rechazado", icon: XCircle, color:"text-rose-600 bg-rose-50 border-rose-200" },
};

export default function DocumentDetail({ embeddedId, onBack }) {
  const params = useParams();
  const id = embeddedId || params.id;
  const { user, can } = useAuth();
  const [data, setData] = useState(null);
  const [perms, setPerms] = useState(null);
  const [showViewer, setShowViewer] = useState(false);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [showResubmit, setShowResubmit] = useState(false);
  const [showEdit, setShowEdit] = useState(false);

  const reload = () => Promise.all([
    api.get(`/documents/${id}/timeline`).then(r => setData(r.data)),
    api.get(`/documents/${id}/can-approve`).then(r => setPerms(r.data)).catch(()=>setPerms(null)),
  ]);
  useEffect(() => { reload(); }, [id]);

  if (!data) return <div className="text-center py-16 text-neutral-400">Cargando...</div>;
  const d = data.document;
  const statusLabel = { vigente: "Vigente", proximo: "Próximo a vencer", vencido: "Vencido", pendiente: "Pendiente" }[d.status];
  const approvalKey = d.approval_status || "pendiente";
  const approvalStyle = APPROVAL_STYLES[approvalKey];
  const canAct = perms?.can_act === true;
  const canResubmit = perms?.can_resubmit === true;

  const act = async (action) => {
    setBusy(true);
    try {
      await api.post(`/documents/${id}/${action}`, { comment });
      toast.success(action === "approve" ? "Documento aprobado" : "Documento rechazado");
      setComment("");
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };

  const timeline = [
    ...data.history.map(h => ({ type: "audit", date: h.timestamp, label: h.details, user: h.user_name })),
    ...data.events.map(e => ({ type: "event", date: e.event_date + "T00:00:00", label: e.title, level: e.alarm_level })),
  ].sort((a,b) => new Date(b.date) - new Date(a.date));

  return (
    <div className="space-y-6 max-w-5xl" data-testid="document-detail-page">
      {onBack
        ? <button onClick={onBack} className="inline-flex items-center gap-2 text-sm text-neutral-500 hover:text-neutral-900" data-testid="document-back-btn"><ArrowLeft size={16}/> Volver</button>
        : <Link to="/documentos" className="inline-flex items-center gap-2 text-sm text-neutral-500 hover:text-neutral-900"><ArrowLeft size={16}/> Volver a documentos</Link>}

      <div className="bg-white border border-neutral-200 rounded-md p-6">
        <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 bg-blue-50 text-blue-700 rounded-md flex items-center justify-center border border-blue-100"><FileText size={26} weight="duotone"/></div>
            <div>
              <div className="text-xs uppercase tracking-widest text-neutral-500 font-semibold">Seguimiento documental</div>
              <h1 className="font-display text-3xl font-black tracking-tighter mt-1">{d.name}</h1>
              <p className="text-sm text-neutral-500">{d.filename}</p>
            </div>
          </div>
          <div className="flex flex-col gap-2 items-end">
            <span className={`px-3 py-1.5 rounded-md text-sm font-bold uppercase tracking-wide border ${STATUS_STYLES[d.status]}`} data-testid="doc-status-badge">{statusLabel}</span>
            <span className={`px-3 py-1.5 rounded-md text-sm font-bold uppercase tracking-wide border ${approvalStyle.bg} ${approvalStyle.text} ${approvalStyle.border}`} data-testid="doc-approval-badge">{approvalStyle.label}</span>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-4 text-sm">
          <Info label="Periodo" value={d.period}/>
          <Info label="Autoridad" value={d.authority}/>
          <Info label="Género" value={d.genre}/>
          <Info label="Emisión" value={d.emission_date || "—"}/>
          <Info label="Vigencia" value={d.validity_date || "—"}/>
          <Info label="Vencimiento" value={d.expiration_date || "—"} highlight={d.status==="vencido"||d.status==="proximo"}/>
          <Info label="Responsable" value={d.responsible || "—"} icon={UserIcon}/>
          <Info label="Categoría" value={d.category || "—"}/>
          <Info label="Tipo" value={d.doc_type || "—"}/>
        </div>

        {(data.installation || data.obligation) && (
          <div className="mt-6 pt-6 border-t border-neutral-200 grid md:grid-cols-2 gap-4">
            {data.installation && <Card icon={Factory} title="Instalación" name={data.installation.name} subtitle={data.installation.type}/>}
            {data.obligation && <Card icon={Scales} title="Obligación" name={data.obligation.name} subtitle={`${data.obligation.authority} · ${data.obligation.frequency}`}/>}
          </div>
        )}

        {d.observations && <div className="mt-6 pt-6 border-t border-neutral-200"><div className="text-xs uppercase tracking-widest text-neutral-500 font-semibold mb-2">Observaciones</div><p className="text-sm text-neutral-700">{d.observations}</p></div>}

        <div className="mt-6 pt-6 border-t border-neutral-200 flex flex-wrap gap-2">
          <button onClick={()=>setShowViewer(s=>!s)} className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold rounded-md" data-testid="toggle-viewer-btn">
            <Eye size={16}/> {showViewer ? "Ocultar previsualización" : "Previsualizar archivo"}
          </button>
          <a href={`/api/documents/file/${d.drive_file_id}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-md" data-testid="download-doc-btn">
            <DownloadSimple size={16}/> Descargar archivo
          </a>
          {can("documents", "write") && (approvalKey === "pendiente" || approvalKey === "rechazado") && (
            <button onClick={()=>setShowEdit(true)} className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold rounded-md" data-testid="edit-doc-btn">
              <PencilSimple size={16}/> Editar metadatos
            </button>
          )}
        </div>
      </div>

      {showViewer && (
        <div className="bg-white border border-neutral-200 rounded-md p-4" data-testid="doc-viewer-section">
          <DocumentViewerInline docId={d.id} fileId={d.drive_file_id} mimeType={d.mime_type} filename={d.filename}/>
        </div>
      )}

      {/* Approval workflow */}
      <div className="bg-white border border-neutral-200 rounded-md p-6" data-testid="approval-section">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
          <h2 className="font-display text-xl font-bold tracking-tight flex items-center gap-2"><ShieldCheck size={20} weight="duotone" className="text-emerald-600"/> Flujo de aprobación
            {d.current_attempt > 1 && <span className="ml-2 px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded text-xs font-semibold border border-indigo-100" data-testid="attempt-counter">Intento #{d.current_attempt}</span>}
          </h2>
          {(perms?.approvers?.length > 0 || perms?.approver_name) && (
            <div className="text-xs text-neutral-500">
              Aprobadores asignados:{" "}
              {perms?.approvers?.length > 0 ? (
                <span className="font-semibold text-neutral-900">
                  {perms.approvers.map(a => a.name).join(", ")}
                </span>
              ) : (
                <span className="font-semibold text-neutral-900">{perms.approver_name}</span>
              )}
              {perms?.approvers?.length > 1 && (
                <span className="ml-1 text-neutral-400">(cualquiera puede aprobar)</span>
              )}
            </div>
          )}
        </div>

        {approvalKey === "aprobado" && (
          <div className="space-y-2 mb-4 p-4 bg-emerald-50/50 border border-emerald-200 rounded-md">
            <div className="text-sm"><span className="text-neutral-500">Resultado:</span> <span className="font-bold text-emerald-700">Aprobado ✓</span></div>
            {d.approver_name && <div className="text-sm text-neutral-600">Por: <span className="font-semibold text-neutral-900">{d.approver_name}</span> ({d.approver_email})</div>}
            {d.approval_at && <div className="text-xs text-neutral-500">{new Date(d.approval_at).toLocaleString("es-MX",{dateStyle:"medium",timeStyle:"short"})}</div>}
            {d.approval_comment && <div className="mt-3 p-3 bg-white rounded-md text-sm text-neutral-700"><span className="text-xs uppercase tracking-widest text-neutral-500 font-semibold block mb-1">Comentario</span>{d.approval_comment}</div>}
          </div>
        )}

        {approvalKey === "rechazado" && (
          <div className="space-y-3 mb-4 p-4 bg-rose-50/50 border border-rose-200 rounded-md">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-sm"><span className="text-neutral-500">Resultado:</span> <span className="font-bold text-rose-700">Rechazado ✗</span></div>
                {d.approver_name && <div className="text-sm text-neutral-600">Por: <span className="font-semibold text-neutral-900">{d.approver_name}</span></div>}
                {d.approval_at && <div className="text-xs text-neutral-500">{new Date(d.approval_at).toLocaleString("es-MX",{dateStyle:"medium",timeStyle:"short"})}</div>}
              </div>
              {canResubmit && (
                <button onClick={()=>setShowResubmit(true)} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-md" data-testid="resubmit-btn">
                  <ArrowsClockwise size={16}/> Subir versión corregida
                </button>
              )}
            </div>
            {d.approval_comment && <div className="p-3 bg-white rounded-md text-sm text-neutral-700"><span className="text-xs uppercase tracking-widest text-neutral-500 font-semibold block mb-1">Motivo del rechazo</span>{d.approval_comment}</div>}
          </div>
        )}

        {approvalKey === "pendiente" && canAct && (
          <div className="space-y-3">
            <p className="text-sm text-neutral-500">Documento pendiente de revisión. Como aprobador asignado, puedes aprobar o rechazar.</p>
            <textarea value={comment} onChange={e=>setComment(e.target.value)} rows={2} placeholder="Comentario (opcional para aprobar, recomendado para rechazar)" className="w-full px-3 py-2 border border-neutral-200 rounded-md text-sm" data-testid="approval-comment"/>
            <div className="flex gap-2">
              <button onClick={()=>act("approve")} disabled={busy} className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold rounded-md disabled:opacity-60" data-testid="approve-btn">
                <CheckCircle size={16}/> Aprobar
              </button>
              <button onClick={()=>act("reject")} disabled={busy} className="inline-flex items-center gap-2 px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold rounded-md disabled:opacity-60" data-testid="reject-btn">
                <XCircle size={16}/> Rechazar
              </button>
            </div>
          </div>
        )}

        {approvalKey === "pendiente" && !canAct && (
          <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
            {(perms?.approvers?.length > 0 || perms?.approver_id)
              ? <>⏳ Esperando revisión de <strong>{perms?.approvers?.length > 1 ? `${perms.approvers.length} aprobadores` : perms.approver_name}</strong>.</>
              : <>⚠️ No hay aprobadores configurados para esta empresa. Configúralos en Perfil de Empresa.</>}
          </p>
        )}

        {/* Approval history timeline */}
        {Array.isArray(d.approval_history) && d.approval_history.length > 0 && (
          <div className="mt-6 pt-5 border-t border-neutral-200">
            <div className="text-xs uppercase tracking-widest text-neutral-500 font-semibold mb-3">Historial de validación ({d.approval_history.length} {d.approval_history.length===1?"entrada":"entradas"})</div>
            <ul className="space-y-2" data-testid="approval-history">
              {[...d.approval_history].reverse().map((h, idx) => {
                const cfg = ACTION_LABELS[h.action] || { label: h.action, icon: Clock, color: "text-neutral-600 bg-neutral-50 border-neutral-200" };
                const Icon = cfg.icon;
                return (
                  <li key={idx} className={`p-3 rounded-md border ${cfg.color.split(' ').slice(1).join(' ')}`} data-testid={`history-entry-${idx}`}>
                    <div className="flex items-start gap-3">
                      <div className={`w-8 h-8 rounded-full bg-white border-2 ${cfg.color.split(' ').slice(2).join(' ')} flex items-center justify-center flex-shrink-0`}>
                        <Icon size={14} className={cfg.color.split(' ')[0]} weight="bold"/>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-baseline gap-x-2">
                          <span className={`font-semibold ${cfg.color.split(' ')[0]}`}>{cfg.label}</span>
                          {h.attempt && <span className="text-xs text-neutral-500">Intento #{h.attempt}</span>}
                        </div>
                        <div className="text-xs text-neutral-600">por <span className="font-semibold">{h.by_user_name}</span> ({h.by_user_email})</div>
                        <div className="text-xs text-neutral-500">{h.timestamp && new Date(h.timestamp).toLocaleString("es-MX",{dateStyle:"medium",timeStyle:"short"})}</div>
                        {h.comment && <div className="mt-1.5 text-sm text-neutral-700 italic">"{h.comment}"</div>}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>

      {data.events.length > 0 && (
        <div className="bg-white border border-neutral-200 rounded-md p-6">
          <h2 className="font-display text-xl font-bold tracking-tight mb-4 flex items-center gap-2"><Bell size={20} weight="duotone" className="text-amber-600"/> Alarmas asociadas</h2>
          <ul className="space-y-2" data-testid="doc-alarms">
            {data.events.map(e => (
              <li key={e.id} className="flex items-center gap-3 p-3 bg-neutral-50 rounded-md">
                <span className={`w-2 h-8 rounded-sm ${ALARM_COLORS[e.alarm_level]||'bg-neutral-400'}`}></span>
                <div className="flex-1">
                  <div className="font-semibold text-sm">{e.title}</div>
                  <div className="text-xs text-neutral-500">{e.event_date} · {e.alarm_level||"manual"}</div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="bg-white border border-neutral-200 rounded-md p-6">
        <h2 className="font-display text-xl font-bold tracking-tight mb-4 flex items-center gap-2"><Clock size={20} weight="duotone" className="text-indigo-600"/> Línea de tiempo</h2>
        {timeline.length === 0 ? <p className="text-sm text-neutral-400 py-6 text-center">Sin actividad registrada.</p> :
          <ul className="space-y-3 border-l-2 border-neutral-200 pl-5" data-testid="doc-timeline">
            {timeline.map((t,i)=>(
              <li key={i} className="relative">
                <span className="absolute -left-[27px] top-1 w-3 h-3 rounded-full bg-blue-500 border-2 border-white"></span>
                <div className="text-xs text-neutral-500">{new Date(t.date).toLocaleString("es-MX",{dateStyle:"medium",timeStyle:"short"})}</div>
                <div className="text-sm font-semibold text-neutral-900">{t.label}</div>
                {t.user && <div className="text-xs text-neutral-500">por {t.user}</div>}
                {t.level && <span className="inline-block mt-1 px-2 py-0.5 bg-amber-50 text-amber-700 rounded text-xs">{t.level}</span>}
              </li>
            ))}
          </ul>
        }
      </div>
      {showResubmit && <ResubmitModal docId={id} onClose={()=>setShowResubmit(false)} onSuccess={()=>{ setShowResubmit(false); reload(); }}/>}
      {showEdit && <DocumentEditModal document={d} onClose={()=>setShowEdit(false)} onSuccess={()=>{ setShowEdit(false); reload(); }}/>}
    </div>
  );
}

function ResubmitModal({ docId, onClose, onSuccess }) {
  const [file, setFile] = useState(null);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!file) return toast.error("Selecciona el archivo corregido");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("comment", comment);
      await api.post(`/documents/${docId}/resubmit`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Documento reenviado, estatus: Pendiente de aprobación");
      onSuccess();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo reenviar");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" data-testid="resubmit-modal">
      <div className="bg-white rounded-md w-full max-w-lg border border-neutral-200">
        <div className="flex items-center justify-between p-5 border-b border-neutral-200">
          <h3 className="font-display text-lg font-bold flex items-center gap-2"><UploadSimple size={18} className="text-blue-600"/> Subir versión corregida</h3>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-700">✕</button>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-sm text-neutral-600 bg-blue-50 border border-blue-200 rounded-md p-3">
            Al subir el archivo corregido, el documento volverá a <strong>"Pendiente de aprobación"</strong> y el aprobador será notificado para revisar el nuevo intento.
          </p>
          <div>
            <label className="block text-xs uppercase tracking-widest font-semibold text-neutral-700 mb-2">Archivo corregido</label>
            <input type="file" onChange={e=>setFile(e.target.files?.[0]||null)} className="w-full border border-neutral-200 rounded-md p-2 text-sm" data-testid="resubmit-file-input"/>
            {file && <p className="text-xs text-neutral-500 mt-1">{file.name} · {(file.size/1024).toFixed(1)} KB</p>}
          </div>
          <div>
            <label className="block text-xs uppercase tracking-widest font-semibold text-neutral-700 mb-2">Comentario / cambios realizados (opcional)</label>
            <textarea value={comment} onChange={e=>setComment(e.target.value)} rows={3} placeholder="Ej: Se corrigió fecha de emisión y se actualizó la firma del responsable." className="w-full px-3 py-2 border border-neutral-200 rounded-md text-sm" data-testid="resubmit-comment"/>
          </div>
        </div>
        <div className="flex justify-end gap-2 p-4 border-t border-neutral-200 bg-neutral-50">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-neutral-300 rounded-md">Cancelar</button>
          <button onClick={submit} disabled={busy||!file} className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-md disabled:opacity-60" data-testid="resubmit-submit">
            {busy ? "Subiendo..." : "Reenviar a aprobación"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Info({ label, value, highlight, icon: Icon }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-widest text-neutral-500 font-semibold mb-1 flex items-center gap-1.5">{Icon && <Icon size={12}/>}{label}</div>
      <div className={`font-semibold ${highlight?'text-rose-600':'text-neutral-900'}`}>{value}</div>
    </div>
  );
}
function Card({ icon: Icon, title, name, subtitle }) {
  return (
    <div className="flex items-center gap-3 p-4 bg-neutral-50 rounded-md">
      <div className="w-10 h-10 bg-white border border-neutral-200 rounded-md flex items-center justify-center text-indigo-700"><Icon size={18} weight="duotone"/></div>
      <div><div className="text-xs uppercase tracking-widest text-neutral-500 font-semibold">{title}</div><div className="font-semibold">{name}</div>{subtitle && <div className="text-xs text-neutral-500">{subtitle}</div>}</div>
    </div>
  );
}
